'use strict';

import Alerts from 'lib/alerts';

import PayrollItemSelectionModalView from '../PayrollItemSelectionModal.js';

import template from '../../templates/employee/YearlyTotalsModal.ejs';
import ItemView from './YearlyTotalRow.js';
import moment from 'moment';
import loader from 'helpers/loader.js';
import modal from 'helpers/modal.js';

import Payroll from 'newModels/payroll.js';

module.exports = Backbone.View.extend({
    tagName: 'div',

    itemViews: [],

    availablePayrollItems: null,

    events: {
        'click .js-save':     'save',
        'click .js-cancel':   'cancel',
        'click .js-reset':    'reset',
        'click .js-autofill': 'autoFill',
        'click .js-add-item': 'showSelectModal',

        'shown.bs.modal':     'focusFirstInput'
    },

    initialize: function(options) {
        this.options = _.defaults(options || {}, {
            current:      true,
            payrollItems: null,
            year: null
        });

        this.availablePayrollItems = this.options.payrollItems.clone();

        if (this.collection) {
            this.bindHandlers();
            this.updateItemAvailability();
        }

        this.payrollItemSelectionModal = new PayrollItemSelectionModalView({
            collection: this.availablePayrollItems
        });
        this.payrollItemSelectionModal.on('select', this.addItems, this);

        this.render();
    },

    bindHandlers: function() {
        this.collection.on('add', this.renderItem, this);
        this.collection.on('sync reset', this.renderItems, this);
        this.collection.on('remove', this.removeItemView, this);
        this.collection.on('add remove sync', this.updateItemsTabIndex, this);
        this.collection.on(
            'add remove sync', this.updateItemAvailability, this
        );

        return this;
    },

    setCollection: function(collection) {
        this.collection = collection;

        this.bindHandlers();

        this.render();
        this.renderItems();

        this.updateItemAvailability();
    },

    getUserMonthLabels: function() {
        let now         = moment();
        let joinDate    = moment(this.model.get('commencementDate') || now);
        let periodStart = moment(Payroll.getCurrentPeriodDate());
        let yearStart   = moment(periodStart).utc().date(1).month(0);
        let range;

        if (yearStart.isAfter(joinDate)) {
            joinDate = moment(new Date(yearStart.year(), 0, 1, 12));
        }

        if (this.options.current) {
            range = _.range(
                Math.min(joinDate.month(), periodStart.month()),
                periodStart.month()
            );
        } else if (
            joinDate.month() > periodStart.month() &&
            joinDate.month() >= now.month()
        ) {
            range = _.range(periodStart.month() + 1);
        } else {
            let month = Math.min(joinDate.month(), periodStart.month());
            //increase month by 1 if employee started mid month
            month = Math.min(12, month + (joinDate.date() > 1));
            range = _.range(month);
        }

        return _(range).map(month => Object.create({
            index: month,
            label: moment.monthsShort()[month]
        }));
    },

    /**
     * Get an array of months to display in the modal based on the join
     * date of the employee and whether this modal is displaying current or
     * previous employment data.
     *
     * For current employment, we show the months starting from the join date
     * up to the current period (not including it). If the employee joined
     * sometime in april and today is 1st of october, then we show
     * april - august (as september is the current period until the 15th of oct)
     *
     * For previous employment we show the months starting from january up to
     * the join date, including or excluding it depending on whether it is
     * larger than the current period or calendar month. Assuming today is 14th
     * of october and the employee joins the 1st of october, we show january to
     * september (september is the currently active payroll, but because the
     * employee joined at the start of calendar october it will be excluded from
     * it, hence we show the column)
     *
     * @returns {Object[]} An array of objects with index and label attributes
     *  representing the index of the month (zero based) and its label
     */
    getMonthLabels: function() {

        return _.range(12).map(month => Object.create({
            index: month,
            label: moment.monthsShort()[month]
        }));

    },

    render: function() {
        this.$el.addClass('modal inmodal').html(template({
            current:     this.options.current,
            employee:    this.model,
            months:      this.getMonthLabels()
        }));

        this.$el.attr('data-backdrop', 'static');
        this.$el.attr('keyboard', 'false');

        if (this.collection) {
            this.renderItems();
        }

        return this;
    },

    renderItems: function() {
        this.itemViews.forEach(view => view.remove());
        this.itemViews = [];

        this.$('.items').toggleClass('hidden', !this.collection.length);
        this.$('.js-empty').toggleClass('hidden', !!this.collection.length);
        this.$('.js-button-bar').toggleClass('hidden', !this.collection.length);

        this.collection.forEach(this.renderItem.bind(this));
        this.updateItemsTabIndex();
    },

    renderItem: function(model) {
        const lastMonth = this.getUserMonthLabels()[
            this.getUserMonthLabels().length - 1
        ];

        var itemView = new ItemView({
            current: this.options.current,
            model:   model,
            months:  this.getMonthLabels(),
            stop: lastMonth
        });

        this.itemViews.push(itemView);
        this.$('.items').append(itemView.$el);
    },

    removeItemView: function(model) {
        this.itemViews = _.reject(this.itemViews, function(view) {
            return (view.model === model);
        });
    },

    showSelectModal: function() {
        var restoreSelf = function() {
            this.show();
            this.payrollItemSelectionModal.off('hide', restoreSelf);
        }.bind(this);

        this.hide();
        this.payrollItemSelectionModal.on('hide', restoreSelf);
        this.payrollItemSelectionModal.show();
    },

    addItems: function(payrollItems, additionalInfo) {
        if (!this.collection) {
            return;
        }

        payrollItems.forEach(function(payrollItem) {
            const info = _.isObject(additionalInfo) ?
                (additionalInfo[payrollItem.id] || null) : null;

            this.collection.add({
                employeeId: this.model.id,
                payrollItemId: payrollItem.id,
                amount: 0,
                monthly: [],
                additionalInformation: info,
                year: this.options.year
            });
        }.bind(this));
    },

    updateItemAvailability: function() {
        this.availablePayrollItems.reset(
            this.options.payrollItems.clone().filter(model => {
                return !(this.collection.find(itemYearlyTotal => {
                    return itemYearlyTotal.get('payrollItemId') === model.id;
                }));
            }),
            {silent: true}
        );
    },

    updateItemsTabIndex: function() {
        this.itemViews.forEach((view, idx) => {
            view.setTabIndex(idx + 1, this.collection.length);
        });
    },

    focusFirstInput: function() {
        this.$('input').first().focus();
    },

    hide: function() {
        this.$el.modal('hide');
        this.trigger('hide');
    },

    show: function() {
        this.$el.modal('show');
        this.trigger('show');
    },

    resetPayrollConfirm: function() {
        return new Promise((resolve, reject) => {
            if (!_.isObject(this.options.unsubmittedPayroll)) {
                return resolve(true);
            }

            Alerts.show('infoConfirm', {
                title: `Your saved payroll will be modified!`,
                text: `You have an unsubmitted payroll saved. \
                    If you continue, the payroll will be sent to \
                    Step 1 and updated based on the modifications \
                    you are about to save. Please note that any \
                    unsaved payroll adjustments will be lost.`,
                confirmButtonText: `Yes, Save Changes`
            }).then(() => resolve(), err => reject(err));
        });
    },

    save: function(ev) {
        var $button = this.$(ev.currentTarget);
        let stopLoader = () => {};

        ev.preventDefault();

        if (!this.collection.isValid()) {
            return Alerts.show('error', {
                title: 'Can\'t save a form with errors.',
                text:  'Please review the form and fix the highlighted ' +
                    'errors before saving.',
            });
        }

        return this.resetPayrollConfirm().then(() => {
            stopLoader = loader.startFullScreen($button);

            this.hide();

            return this.collection.save().then(() => {
                modal.showAlertSuccess('Your changes have been saved');
            });
        }, () => ({cancel: true}))
            .then(() => {
                if (this.options.unsubmittedPayroll) {
                    return this.options.unsubmittedPayroll
                        .resetEmployee({employee: this.model});
                }

                return Promise.resolve(true);
            })
            .catch(err => {
                stopLoader();
                if ((err || {}).cancel) {
                    return;
                }
                return Alerts.show('criticalError', err);
            }).finally(() => stopLoader());
    },

    cancel: function(ev) {
        var $button = this.$(ev.currentTarget);

        Alerts.show('infoConfirm', {
            title: `Unsaved Changes`,
            text: `You have unsaved changes. Exiting this page \
                will result in the changes being discarded.`,
            confirmButtonText:  'Exit',
            cancelButtonText: 'Stay'
        }).then(() => {
            var stopLoader = loader.startFullScreen($button);
            if (this.collection.isNew()) {
                this.collection.clearAmounts({
                    current: this.options.current
                });
                stopLoader();
            } else {
                this.collection.fetchForPreviousPayslips().finally(stopLoader);
            }
        });
    },

    reset: function() {
        this.hide();

        Alerts.show('dangerConfirm', {
            title:             'Are you sure you want to clear the form?',
            confirmButtonText: 'Yes, Clear',
            callback: isConfirm => {
                this.show();

                if (!isConfirm) {
                    return;
                }

                this.collection.clearAmounts({
                    current: this.options.current
                });
            }
        });
    },

    autoFill: function() {
        let salary = this.collection.find(model => model.isBase());

        if (this.options.current) {
            Alerts.show('error', {
                title: `Can't Auto-Fill Current Employment Details`,
                text:  `The auto-fill feature creates estimates. You can use ` +
                    `to fill in previous employment information if you don't ` +
                    `have that data available to improve tax calculations. ` +
                    `For current employment you should always use real data.`
            });
            return;
        }

        if (
            salary.get('prevEmploymentAmount') === 0  &&
            _.compact(salary.get('prevEmploymentMonthly')).length === 0
        ) {
            Alerts.show('error', {
                title: 'Missing Values',
                text:  'You need to specify salaries to run the auto-fill ' +
                    'function.'
            });

            return;
        }

        Alerts.show('infoConfirm', {
            html: `Estimated PCB, EPF or SOCSO values will be calculated ` +
                `for you, and any values you entered will be overwritten ` +
                `but are editable. <br><br><strong>This function should only ` +
                `be used to improve monthly tax calculations if accurate ` +
                `previous employment data is not available</strong>`,
            confirmButtonText:  'Yes, Auto-Fill'
        }).then(() => this.model.autoFillPreviousPayslipsData({
            itemYearlyTotals: this.collection,
            current: false
        }));
    }
});



// WEBPACK FOOTER //
// ./app/employer/views/employee/YearlyTotalsModal.js